import { useCallback, useEffect, useLayoutEffect, useRef } from 'react'
import { useChatLayout } from '../../context/ChatLayoutContext.jsx'
import {
  findLastVisitorMessage,
  findTurnFirstBotMessage,
  getMessageElement,
  getMessageListPaddingTop,
  isBotOnlyThread,
  isProactiveBotTurn,
  scrollContainerToTop,
  scrollToMessageAnchor,
} from '../../lib/messageListScroll.js'
import { MessageBubble } from './MessageBubble.jsx'
import { TypingIndicator } from './messages/TypingIndicator.jsx'
import { RotatingTypingStatus } from './messages/RotatingTypingStatus.jsx'
import { cn } from '../../lib/cn.js'

/**
 * Heuristic turn anchor for threads we did not observe being appended
 * (history restore on open). Live turns are tracked precisely by
 * MessageList's append effect instead, because BotMan intent replies
 * append several bot rows (text + list carousel) and only the rows of
 * the *current* dispatch belong to the turn.
 *
 * @param {Array<{ id?: string, from?: string, type?: string }>} messages
 * @returns {string | null}
 */
function resolveTurnAnchorHeuristic(messages) {
  if (isProactiveBotTurn(messages) || isBotOnlyThread(messages)) {
    const bot = findTurnFirstBotMessage(messages)
    return bot?.id ?? null
  }
  const visitor = findLastVisitorMessage(messages)
  if (visitor?.id) return visitor.id
  const bot = findTurnFirstBotMessage(messages)
  return bot?.id ?? null
}

/**
 * Scrolling message area.
 *
 * Auto-scroll matches streamed responses: keep the top of the current
 * turn visible while the bot reply (text, carousel, suggestions) grows
 * below the fold. Visitor turns pin the visitor question; BotMan /
 * whisper / engagement turns pin the first bot row of the reply. If the
 * user has scrolled up to read history, do not yank the viewport —
 * except for proactive bot rows, which should always become visible
 * when they arrive.
 */
export function MessageList({
  messages,
  isTyping,
  conf,
  ochatbot,
  layoutKey = 'overlay',
  onSend,
  /** Panel suggestions strip item count (0 when hidden); used to re-pin after layout shifts. */
  panelSuggestionsCount = 0,
}) {
  const { open } = useChatLayout()
  const endRef = useRef(null)
  const containerRef = useRef(null)
  const bottomThreshold = 24
  const isNearBottomRef = useRef(true)
  const lastAutoScrolledBotIdRef = useRef(null)
  /** Row id whose top stays pinned for the current turn. */
  const turnAnchorIdRef = useRef(null)
  /** True between a typing rising edge and the first row of the reply. */
  const awaitingTurnAnchorRef = useRef(false)
  /** Anchor currently being followed; null once the user scrolls up. */
  const pinnedAnchorIdRef = useRef(null)
  const prevAnchorCountRef = useRef(messages.length)
  const prevTypingForAnchorRef = useRef(isTyping)
  const prevMessageCountRef = useRef(messages.length)
  const prevLayoutMessageCountRef = useRef(messages.length)
  const prevIsTypingRef = useRef(isTyping)
  const prevOpenRef = useRef(false)
  const scrollToBottomOnHistoryLoadRef = useRef(false)
  const turnResizeObserverRef = useRef(null)

  // ---- Turn anchor tracking (layout effects run in declaration order,
  // so downstream scroll/observer effects always see the fresh anchor).

  useLayoutEffect(() => {
    if (isTyping && !prevTypingForAnchorRef.current) {
      awaitingTurnAnchorRef.current = true
    }
    prevTypingForAnchorRef.current = isTyping
  }, [isTyping])

  useLayoutEffect(() => {
    const prevCount = prevAnchorCountRef.current
    prevAnchorCountRef.current = messages.length

    if (messages.length === 0) {
      turnAnchorIdRef.current = null
      awaitingTurnAnchorRef.current = false
      return
    }
    if (messages.length <= prevCount) return

    // Thread populated at once (history restore / launch): heuristic.
    if (prevCount === 0) {
      turnAnchorIdRef.current = resolveTurnAnchorHeuristic(messages)
      awaitingTurnAnchorRef.current = false
      return
    }

    let firstNew = null
    for (let i = prevCount; i < messages.length; i += 1) {
      const m = messages[i]
      if (m && m.type !== 'typing_indicator') {
        firstNew = m
        break
      }
    }
    if (!firstNew) return

    if (firstNew.from === 'visitor') {
      const visitor = findLastVisitorMessage(messages)
      turnAnchorIdRef.current = visitor?.id ?? firstNew.id ?? null
      awaitingTurnAnchorRef.current = false
      return
    }

    // Bot row(s) appended.
    if (awaitingTurnAnchorRef.current) {
      awaitingTurnAnchorRef.current = false
      let prevLast = null
      for (let i = prevCount - 1; i >= 0; i -= 1) {
        const m = messages[i]
        if (m && m.type !== 'typing_indicator') {
          prevLast = m
          break
        }
      }
      if (prevLast?.from === 'visitor') {
        // Q&A: keep the visitor question pinned above the reply.
        turnAnchorIdRef.current = prevLast.id ?? null
      } else {
        // Proactive turn (whisper / intent): pin the reply's first row.
        turnAnchorIdRef.current = firstNew.id ?? null
      }
      return
    }

    // Continuation rows of the same dispatch (e.g. carousel after text):
    // keep the existing anchor. Backfill only if we have none.
    if (!turnAnchorIdRef.current) {
      turnAnchorIdRef.current = resolveTurnAnchorHeuristic(messages)
    }
  }, [messages])

  const getTurnAnchorId = useCallback(() => {
    const container = containerRef.current
    const id = turnAnchorIdRef.current
    if (id && container && getMessageElement(container, id)) return id
    return resolveTurnAnchorHeuristic(messages)
  }, [messages])

  // ---- Scroll primitives.

  const scrollToBottom = useCallback((behavior = 'auto') => {
    endRef.current?.scrollIntoView({ block: 'end', behavior })
    isNearBottomRef.current = true
  }, [])

  const scrollToLatestTurn = useCallback(
    (behavior = 'auto') => {
      const container = containerRef.current
      if (!container) return

      const anchorId = getTurnAnchorId()
      if (!anchorId) {
        scrollContainerToTop(container, { behavior })
        pinnedAnchorIdRef.current = null
        return
      }

      const ok = scrollToMessageAnchor(container, anchorId, {
        behavior,
        onFallback: () => scrollToBottom(behavior),
      })
      pinnedAnchorIdRef.current = ok ? anchorId : null
    },
    [getTurnAnchorId, scrollToBottom],
  )

  const scheduleLatestTurnScroll = useCallback(
    (behavior = 'smooth') => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          scrollToLatestTurn(behavior)
        })
      })
    },
    [scrollToLatestTurn],
  )

  const shouldAutoScroll = useCallback(
    () =>
      isBotOnlyThread(messages) ||
      isProactiveBotTurn(messages) ||
      isNearBottomRef.current,
    [messages],
  )

  const resetScrollState = useCallback(() => {
    lastAutoScrolledBotIdRef.current = null
    pinnedAnchorIdRef.current = null
    turnAnchorIdRef.current = null
    awaitingTurnAnchorRef.current = false
    isNearBottomRef.current = true
    turnResizeObserverRef.current?.disconnect()
    turnResizeObserverRef.current = null
    const container = containerRef.current
    if (container) container.scrollTop = 0
  }, [])

  useLayoutEffect(() => {
    const flush = () => {
      // Opening with history: jump to the latest turn (not absolute bottom)
      // so BotMan carousels do not hide their opening text.
      scrollToLatestTurn('auto')
    }
    const scheduleFlush = () => {
      requestAnimationFrame(() => {
        requestAnimationFrame(flush)
      })
    }

    const prevLen = prevLayoutMessageCountRef.current
    prevLayoutMessageCountRef.current = messages.length

    if (!open) {
      prevOpenRef.current = false
      scrollToBottomOnHistoryLoadRef.current = false
      return
    }

    const wasClosed = !prevOpenRef.current
    if (wasClosed) {
      prevOpenRef.current = true
      if (messages.length > 0) {
        scheduleFlush()
      } else {
        scrollToBottomOnHistoryLoadRef.current = true
      }
      return
    }

    if (scrollToBottomOnHistoryLoadRef.current && messages.length > 0) {
      scrollToBottomOnHistoryLoadRef.current = false
      scheduleFlush()
      return
    }

    // Panel stayed open but the thread was cleared then repopulated
    // (e.g. "New Conversation" → launch whisper → engagement question).
    if (prevLen === 0 && messages.length > 0) {
      scheduleFlush()
    }
  }, [open, messages.length, scrollToLatestTurn, messages])

  useEffect(() => {
    const prevCount = prevMessageCountRef.current
    prevMessageCountRef.current = messages.length

    if (messages.length === 0 && prevCount > 0) {
      resetScrollState()
      return
    }

    // Only act on appended messages.
    if (messages.length <= prevCount) return
    if (messages.length === 0) return

    const last = messages[messages.length - 1]
    if (!last) return
    if (last.type === 'typing_indicator') return
    if (!endRef.current) return

    // When the visitor sends a message, always scroll to reveal it.
    if (last.from === 'visitor') {
      requestAnimationFrame(() => {
        scrollToBottom('smooth')
      })
      lastAutoScrolledBotIdRef.current = null
      pinnedAnchorIdRef.current = null
      return
    }

    // Bot message: pin the turn anchor (visitor for Q&A, first bot row
    // for BotMan / whisper). Instant scroll — smooth animation races
    // tall carousels that finish laying out mid-animation.
    if (last.id && lastAutoScrolledBotIdRef.current === last.id) return
    if (!shouldAutoScroll()) return
    const instant = isBotOnlyThread(messages) || isProactiveBotTurn(messages)
    scheduleLatestTurnScroll(instant ? 'auto' : 'smooth')
    lastAutoScrolledBotIdRef.current = last.id ?? null
  }, [
    messages.length,
    messages,
    scheduleLatestTurnScroll,
    scrollToBottom,
    resetScrollState,
    shouldAutoScroll,
  ])

  const lastBotMessage = (() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const msg = messages[i]
      if (!msg || msg.type === 'typing_indicator') continue
      if (msg.from === 'visitor') continue
      return msg
    }
    return null
  })()

  useEffect(() => {
    const wasTyping = prevIsTypingRef.current
    prevIsTypingRef.current = isTyping

    if (!endRef.current) return
    if (!shouldAutoScroll()) return

    if (isTyping) {
      // Keep the current turn anchor visible while waiting.
      scheduleLatestTurnScroll('auto')
      return
    }

    if (wasTyping && !isTyping) {
      // Reply finished: force a re-pin in case the tall BotMan payload
      // finished laying out after the append-time scroll.
      scrollToLatestTurn('auto')
    }
  }, [
    isTyping,
    lastBotMessage?.id,
    scheduleLatestTurnScroll,
    scrollToLatestTurn,
    shouldAutoScroll,
  ])

  const latestBotMessageId = (() => {
    const visible = messages.filter(
      (msg) =>
        msg.type !== 'typing_indicator' &&
        !(conf?.isWebAi && msg.type === 'list'),
    )
    for (let i = visible.length - 1; i >= 0; i -= 1) {
      const msg = visible[i]
      if (msg?.from === 'chatbot') return msg.id
    }
    return null
  })()

  const forcePinTurn = isBotOnlyThread(messages) || isProactiveBotTurn(messages)

  // Keep the turn anchor pinned while BotMan content grows (carousel
  // images, inline suggestions) or the viewport shrinks (suggestions
  // strip mounting). Streamed tokens grow the same way without changing
  // message count; BotMan arrives as tall blocks and needs the same
  // sticky top.
  useLayoutEffect(() => {
    turnResizeObserverRef.current?.disconnect()
    turnResizeObserverRef.current = null

    if (!open) return undefined

    const container = containerRef.current
    if (!container || typeof ResizeObserver !== 'function') return undefined

    const anchorId = getTurnAnchorId()
    if (!anchorId) return undefined

    const anchorEl = getMessageElement(container, anchorId)
    if (!anchorEl) return undefined

    const lastBotEl = getMessageElement(container, latestBotMessageId)

    const maybeRepin = () => {
      const el = containerRef.current
      if (!el) return
      const row = getMessageElement(el, anchorId)
      if (!row) return

      const following =
        forcePinTurn ||
        pinnedAnchorIdRef.current === anchorId ||
        isNearBottomRef.current
      if (!following) return

      const anchorTop = Math.max(0, row.offsetTop - getMessageListPaddingTop(el))
      // Re-pin unless the user scrolled *up* past the anchor to read
      // history. (Browser layout shifts move scrollTop *down* when tall
      // content mounts — those must still re-pin.)
      if (!forcePinTurn && el.scrollTop + bottomThreshold < anchorTop) {
        pinnedAnchorIdRef.current = null
        return
      }
      scrollToMessageAnchor(el, anchorId, { behavior: 'auto' })
      pinnedAnchorIdRef.current = anchorId
    }

    const observer = new ResizeObserver(maybeRepin)
    observer.observe(anchorEl)
    if (lastBotEl && lastBotEl !== anchorEl) observer.observe(lastBotEl)
    observer.observe(container)
    turnResizeObserverRef.current = observer

    // Pin immediately after mount / layout so the first paint of a tall
    // BotMan carousel does not leave the opening line below the fold.
    maybeRepin()

    return () => {
      observer.disconnect()
      if (turnResizeObserverRef.current === observer) {
        turnResizeObserverRef.current = null
      }
    }
  }, [open, forcePinTurn, latestBotMessageId, messages, getTurnAnchorId])

  // Re-pin when the panel suggestions strip mounts/unmounts (it shrinks
  // the scrollport in short widgets).
  useLayoutEffect(() => {
    if (!open) return undefined
    const container = containerRef.current
    if (!container) return undefined
    const anchorId = getTurnAnchorId()
    if (!anchorId) return undefined
    const following =
      forcePinTurn ||
      pinnedAnchorIdRef.current === anchorId ||
      isNearBottomRef.current
    if (!following) return undefined
    scrollToMessageAnchor(container, anchorId, { behavior: 'auto' })
    pinnedAnchorIdRef.current = anchorId
    return undefined
  }, [open, forcePinTurn, panelSuggestionsCount, getTurnAnchorId])

  const visible = messages.filter(
    (msg) =>
      msg.type !== 'typing_indicator' &&
      !(conf?.isWebAi && msg.type === 'list'),
  )

  return (
    <ol
      ref={containerRef}
      onScroll={(e) => {
        const el = e.currentTarget
        const maxScrollTop = el.scrollHeight - el.clientHeight
        const nearBottom =
          maxScrollTop <= 0 ||
          el.scrollTop + el.clientHeight >= el.scrollHeight - bottomThreshold
        isNearBottomRef.current = nearBottom

        const anchorId = pinnedAnchorIdRef.current
        if (anchorId) {
          const row = getMessageElement(el, anchorId)
          if (row) {
            const anchorTop = Math.max(
              0,
              row.offsetTop - getMessageListPaddingTop(el),
            )
            // User scrolled up past the pinned turn — stop following growth.
            if (el.scrollTop + bottomThreshold < anchorTop) {
              pinnedAnchorIdRef.current = null
            }
          }
        }
      }}
      className={cn(
        'ometrics-message-list flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto',
        // Prevent the browser from shifting scroll toward the bottom when
        // a tall BotMan payload (carousel / buttons) mounts at once.
        '[overflow-anchor:none]',
        'px-3 py-3',
      )}
    >
      {visible.map((message, index) => (
        <MessageBubble
          key={message.id}
          message={message}
          conf={conf}
          lastItem={index === visible.length - 1}
          isLatestBotTurn={
            message.from === 'chatbot' && message.id === latestBotMessageId
          }
          ochatbot={ochatbot}
          layoutKey={layoutKey}
          onSend={onSend}
        />
      ))}
      {isTyping && (
        <li className="flex justify-start">
          <div
            className="rounded-2xl rounded-bl-sm bg-[var(--color-muted)] px-3 py-2 shadow-sm"
            style={(() => {
              const bg = conf?.widgetBotMessageBackgroundColor?.trim?.()
              const fg = conf?.widgetButtonColor?.trim?.() || conf?.widgetBotMessageTextColor?.trim?.()
              const s = {}
              if (bg) s.backgroundColor = bg
              if (fg) s.color = fg
              return Object.keys(s).length ? s : undefined
            })()}
          >
            <div className="flex items-center gap-2">
              <TypingIndicator />
              <RotatingTypingStatus conf={conf} />
            </div>
          </div>
        </li>
      )}
      <li ref={endRef} aria-hidden="true" className="h-px" />
    </ol>
  )
}
